import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import {
  useAiSocket,
  type AiAgentErrorEvent,
  type AiAgentMessage,
  type AiAgentMessageListResponse,
  aiAgentChatKeys,
} from '@/entities/ai-agent-chat';

export const useAiAgentStreaming = (chatId: number) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const aiSocket = useAiSocket();

  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingTokens, setStreamingTokens] = useState<string[]>([]);
  /**
   * Сообщения пользователя, которых ещё нет в истории с сервера: то, на которое ждут ответ,
   * и те, ответа на которые уже не будет. Копятся, иначе следующая отправка стирала бы предыдущее из переписки.
   */
  const [pendingUserMessages, setPendingUserMessages] = useState<AiAgentMessage[]>([]);
  const [hasReplyFailed, setHasReplyFailed] = useState(false);

  const lastTsRef = useRef(0);
  /** Ожидание ответа для обработчиков сокета: они живут вне рендера и текущего состояния не видят */
  const isGeneratingRef = useRef(false);
  const isActive = chatId > 0;

  /**
   * Прекращает ожидание ответа, которого уже не будет: сервер сообщил об ошибке, связь оборвалась
   * либо сообщение не удалось отправить.
   * Сообщение пользователя остаётся в переписке — сервер этот обмен не сохранил, и другого следа у него нет.
   */
  const failReply = useCallback(() => {
    if (!isGeneratingRef.current) return;

    isGeneratingRef.current = false;
    setIsGenerating(false);
    setStreamingContent('');
    setStreamingTokens([]);
    setHasReplyFailed(true);
    lastTsRef.current = 0;
  }, []);

  // Неподтверждённые сообщения принадлежат своему чату: после переключения показывать их нельзя
  useEffect(() => {
    isGeneratingRef.current = false;
    setIsGenerating(false);
    setStreamingContent('');
    setStreamingTokens([]);
    setPendingUserMessages([]);
    setHasReplyFailed(false);
    lastTsRef.current = 0;
  }, [chatId]);

  // Подписка на стриминг токенов
  useEffect(() => {
    if (!isActive) return;

    const handleToken = (data: { chatId: number; token: string; ts: number }) => {
      if (data.chatId !== chatId) return;
      if (data.ts <= lastTsRef.current) return;

      lastTsRef.current = data.ts;
      setStreamingContent((prev) => prev + data.token);
      setStreamingTokens((prev) => [...prev, data.token]);
    };

    aiSocket.on('ai_agent:message', handleToken);
    return () => {
      aiSocket.off('ai_agent:message', handleToken);
    };
  }, [aiSocket, chatId, isActive]);

  // Подписка на завершение ответа
  useEffect(() => {
    if (!isActive) return;

    const handleComplete = (data: { chatId: number; message: AiAgentMessage; userMessage: AiAgentMessage }) => {
      if (data.chatId !== chatId) return;

      // Оптимистичное обновление кеша перед сбросом стриминга,
      // чтобы избежать мигания контента между стримингом и кешированным сообщением.
      // Состоявшийся обмен встаёт в начало первой порции: она берётся с конца переписки
      queryClient.setQueryData(
        aiAgentChatKeys.messages(chatId),
        (old: InfiniteData<AiAgentMessageListResponse> | undefined) => {
          if (!old) return { pages: [{ total: 2, items: [data.message, data.userMessage] }], pageParams: [0] };

          const hasUserMessage = old.pages.some((page) =>
            page.items.some((item: AiAgentMessage) => item.id === data.userMessage.id),
          );
          const added = hasUserMessage ? [data.message] : [data.message, data.userMessage];

          return {
            ...old,
            pages: old.pages.map((page, index) => ({
              total: page.total + added.length,
              items: index === 0 ? [...added, ...page.items] : page.items,
            })),
          };
        },
      );

      isGeneratingRef.current = false;
      setIsGenerating(false);
      setStreamingContent('');
      setStreamingTokens([]);
      // Список очищается целиком: состоявшийся обмен пришёл с сервера, а безответные сообщения до него
      // сервер не сохранил — оставь мы их, они встали бы в переписке после более поздних сообщений
      setPendingUserMessages([]);
      setHasReplyFailed(false);
      lastTsRef.current = 0;

      // Фоновое обновление для консистентности данных
      queryClient.invalidateQueries({ queryKey: aiAgentChatKeys.messages(chatId) });
      queryClient.invalidateQueries({ queryKey: aiAgentChatKeys.lists() });
    };

    aiSocket.on('ai_agent:message_complete', handleComplete);
    return () => {
      aiSocket.off('ai_agent:message_complete', handleComplete);
    };
  }, [aiSocket, chatId, isActive, queryClient]);

  // Подписка на сообщение сервера о том, что ответ подготовить не удалось
  useEffect(() => {
    if (!isActive) return;

    const handleError = (data: AiAgentErrorEvent) => {
      if (data.chatId !== chatId) return;

      failReply();
    };

    aiSocket.on('ai_agent:error', handleError);
    return () => {
      aiSocket.off('ai_agent:error', handleError);
    };
  }, [aiSocket, chatId, failReply, isActive]);

  // Подписка на разрыв связи: ответ, которого ждали, уже не придёт
  useEffect(() => {
    if (!isActive) return;

    aiSocket.onDisconnect(failReply);
    return () => {
      aiSocket.offDisconnect(failReply);
    };
  }, [aiSocket, failReply, isActive]);

  const sendChatMessage = useCallback(
    (message: string) => {
      const userMessage: AiAgentMessage = {
        id: -Date.now(),
        role: 'USER',
        content: message,
        createdAt: new Date().toISOString(),
      };

      setPendingUserMessages((prev) => [...prev, userMessage]);
      isGeneratingRef.current = true;
      setIsGenerating(true);
      setStreamingContent('');
      setStreamingTokens([]);
      setHasReplyFailed(false);
      lastTsRef.current = 0;

      const isSent = aiSocket.sendMessage({ chatId, message, pathname: location.pathname + location.search });

      // Соединения нет — сообщение никуда не ушло, и ждать по нему нечего
      if (!isSent) {
        failReply();
      }
    },
    [aiSocket, chatId, failReply, location.pathname, location.search],
  );

  return {
    isGenerating,
    streamingContent,
    streamingTokens,
    pendingUserMessages,
    hasReplyFailed,
    sendChatMessage,
  };
};
